import { useMemo, useState, useEffect } from "react";
import * as S from "./Search.styles";
import SingleProduct from "../ecommerce/singleProduct/SingleProduct";

const INITIAL_PAGE = 16;
const PAGE_STEP = 16;

const SearchUI = ({
  router,
  viewType,
  searchList,
  dummyList,
  cateViewStats,
  moreProducts,
  suggestions = [],
  // ✅ 부모에서 주입되는 게시글 이동 함수
  moveToDetail,
}) => {
  const productGroups = searchList?.data ?? {};
  const hasData = productGroups && Object.keys(productGroups).length > 0;
  const hasCommunity =
    Array.isArray(searchList?.community?.data) &&
    searchList.community.data.length > 0;

  // ===== 추천 키워드 클릭 -> 쿼리 업데이트 =====
  const handleSuggestClick = (kw) => {
    if (!kw) return;
    const { garo, sero, high, erange, ...rest } = router.query;
    const next = { ...rest, search: kw };

    // 로컬 필터 초기화
    setGaroInput("");
    setSeroInput("");
    setHighInput("");
    setSizeTol("30");

    router.push({ pathname: "/search", query: next });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // ===== 상태 =====
  const [innerKeyword, setInnerKeyword] = useState("");
  const [selectedTopCats, setSelectedTopCats] = useState([]); // 1차 카테고리(다중)
  const [searchTags, setSearchTags] = useState([]);           // 2차 카테고리(다중)

  // 사이즈 + 오차(기본 ±30)
  const [garoInput, setGaroInput] = useState("");
  const [seroInput, setSeroInput] = useState("");
  const [highInput, setHighInput] = useState("");
  const [sizeTol, setSizeTol] = useState("30");

  // 정렬
  const [sortKey, setSortKey] = useState("nameAsc");

  // 카테고리별 더보기 상태
  const [catShowCounts, setCatShowCounts] = useState({});

  // URL 변경 시 필터 일부 초기화 + 더보기 리셋
  useEffect(() => {
    resetFilters({ keepKeyword: true, keepSort: true });
    setCatShowCounts({});
  }, [router?.asPath]);

  // productGroups 변경 시 카테고리별 초기 표시 개수 설정
  useEffect(() => {
    if (!hasData) return;
    setCatShowCounts((prev) => {
      const next = { ...prev };
      Object.keys(productGroups).forEach((k) => {
        if (typeof next[k] !== "number") next[k] = INITIAL_PAGE;
      });
      return next;
    });
  }, [productGroups, hasData]);

  const CONTROL_HEIGHT = 36;
  const controlStyle = {
    height: CONTROL_HEIGHT,
    padding: "6px 10px",
    border: "1px solid #e5e7eb",
    borderRadius: 8,
  };

  const resetFilters = ({ keepKeyword = false, keepSort = false } = {}) => {
    if (!keepKeyword) setInnerKeyword("");
    setSelectedTopCats([]);
    setSearchTags([]);
    setGaroInput("");
    setSeroInput("");
    setHighInput("");
    setSizeTol("30");
    if (!keepSort) setSortKey("nameAsc");
  };

  // ===== 유틸 =====
  const norm = (v) => (v ?? "").toString().toLowerCase().trim();
  const includesAll = (hay, needles) => needles.every((n) => hay.includes(n));
  const parsePositive = (val) => {
    const m = (val ?? "").toString().match(/^\s*\d+\s*$/);
    if (!m) return undefined;
    const n = Number(m[0].trim());
    return n > 0 ? n : undefined;
  };

  const sizeMatch = (item) => {
    const wantG = parsePositive(garoInput);
    const wantS = parsePositive(seroInput);
    const wantH = parsePositive(highInput);
    const tol = Number(sizeTol || 0);

    const within = (key, want) => {
      if (typeof want === "number") {
        const t = Number(item?.[key]);
        if (!Number.isFinite(t)) return false;
        return Math.abs(t - want) <= tol;
      }
      return true;
    };

    return within("horizontal", wantG) && within("vertical", wantS) && within("height", wantH);
  };

  const keywordMatch = (item) => {
    const q = norm(innerKeyword);
    if (!q) return true;
    const hay = [item?.pname, item?.brand_kor, item?.feature, item?.category_tag, item?.pcode]
      .map(norm)
      .join(" ");
    const tokens = q.split(/\s+/).filter(Boolean);
    return includesAll(hay, tokens);
  };

  const tagMatch = (item) => {
    if (searchTags.length === 0) return true;
    const tags = (item?.category_tag || "")
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    return tags.some((t) => searchTags.includes(t));
  };

  // 1차 카테고리 후보
  const allTopCats = useMemo(() => Object.keys(productGroups).sort(), [productGroups]);

  // (선택된 1차 범위 내) 평탄화
  const flatInTopScope = useMemo(() => {
    if (!hasData) return [];
    const keys = selectedTopCats.length ? selectedTopCats : Object.keys(productGroups);
    const acc = [];
    keys.forEach((k) => (productGroups[k] || []).forEach((p) => acc.push(p)));
    return acc;
  }, [productGroups, hasData, selectedTopCats]);

  // 2차 카테고리 후보
  const allSecondTags = useMemo(() => {
    const set = new Set();
    flatInTopScope.forEach((p) => {
      (p?.category_tag || "")
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean)
        .forEach((t) => set.add(t));
    });
    return Array.from(set).sort();
  }, [flatInTopScope]);

  // 그룹별 필터 + 정렬
  const filteredGroups = useMemo(() => {
    if (!hasData) return {};

    const applySort = (arr) => {
      const out = [...arr];

      switch (sortKey) {
        case "nameAsc":
          out.sort((a, b) =>
            (a?.pname || "").localeCompare(b?.pname || "", "ko", {
              numeric: true,
              sensitivity: "base",
            })
          );
          break;

        case "priceAsc":
          out.sort((a, b) => Number(a.discount_price) - Number(b.discount_price));
          break;

        case "priceDesc":
          out.sort((a, b) => Number(b.discount_price) - Number(a.discount_price));
          break;

        case "salesDesc":
          out.sort((a, b) => Number(b.hit || 0) - Number(a.hit || 0));
          break;

        case "latest":
          out.sort((a, b) => new Date(b.wdate || 0) - new Date(a.wdate || 0));
          break;

        default:
          break;
      }

      return out;
    };

    const next = {};
    const keys = selectedTopCats.length ? selectedTopCats : Object.keys(productGroups);

    keys.forEach((k) => {
      const src = Array.isArray(productGroups[k]) ? productGroups[k] : [];
      const filtered = src.filter((it) => keywordMatch(it) && tagMatch(it) && sizeMatch(it));
      if (filtered.length) next[k] = applySort(filtered);
    });

    return next;
  }, [
    productGroups,
    hasData,
    selectedTopCats,
    innerKeyword,
    searchTags,
    garoInput,
    seroInput,
    highInput,
    sizeTol,
    sortKey,
  ]);

  const filteredTotal = useMemo(
    () => Object.values(filteredGroups).reduce((sum, arr) => sum + arr.length, 0),
    [filteredGroups]
  );

  // 토글/초기화
  const toggleTopCat = (name) =>
    setSelectedTopCats((prev) => (prev.includes(name) ? prev.filter((t) => t !== name) : [...prev, name]));
  const toggleTag = (tag) =>
    setSearchTags((prev) => (prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]));

  // ===== 정렬 탭 (반응형) =====
  const SortTabs = () => {
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
      const handleResize = () => setIsMobile(window.innerWidth <= 768);
      handleResize();
      window.addEventListener("resize", handleResize);
      return () => window.removeEventListener("resize", handleResize);
    }, []);

    const sortOptions = [
      { id: "nameAsc", label: "이름순" },
      { id: "priceAsc", label: "낮은가격" },
      { id: "priceDesc", label: "높은가격" },
      { id: "salesDesc", label: "인기" },
      { id: "latest", label: "최신" },
    ];

    if (isMobile) {
      return (
        <div style={{ display: "flex", justifyContent: "flex-end", width: "100%" }}>
          <select
            value={sortKey}
            onChange={(e) => setSortKey(e.target.value)}
            style={{
              width: "auto",
              maxWidth: "100%",
              padding: "6px 28px 6px 10px",
              border: "1px solid #e5e7eb",
              borderRadius: 8,
              fontSize: 14,
              color: "#374151",
              background: "#fff",
              whiteSpace: "nowrap",
              appearance: "menulist",
            }}
          >
            {sortOptions.map((opt) => (
              <option key={opt.id} value={opt.id}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      );
    }

    const Tab = ({ id, label }) => {
      const active = sortKey === id;
      return (
        <button
          type="button"
          onClick={() => setSortKey(id)}
          style={{
            background: "transparent",
            border: "none",
            padding: "0 10px",
            color: active ? "#2563eb" : "#6b7280",
            fontWeight: active ? 700 : 500,
            cursor: "pointer",
            whiteSpace: "nowrap",
          }}
        >
          {active ? "✓ " : ""}
          {label}
        </button>
      );
    };

    return (
      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
        <Tab id="nameAsc" label="이름순" />
        <span style={{ color: "#d1d5db" }}>|</span>
        <Tab id="priceAsc" label="낮은가격" />
        <span style={{ color: "#d1d5db" }}>|</span>
        <Tab id="priceDesc" label="높은가격" />
        <span style={{ color: "#d1d5db" }}>|</span>
        <Tab id="salesDesc" label="인기" />
        <span style={{ color: "#d1d5db" }}>|</span>
        <Tab id="latest" label="최신" />
      </div>
    );
  };

  // 카테고리별 더보기
  const showMoreProducts = (catKey) => {
    setCatShowCounts((prev) => ({
      ...prev,
      [catKey]: Math.min((prev[catKey] || INITIAL_PAGE) + PAGE_STEP, (filteredGroups[catKey] || []).length),
    }));
  };

  // ===== 렌더 =====
  return (
    <S.Container>
      <div className="container">
        {/* 검색결과가 있을 때만: 타이틀 + 필터패널 */}
        {hasData && (
          <>
            <div style={{ textAlign: "center", marginTop: 8 }}>
              <h3
                style={{
                  fontSize: 23,
                  fontWeight: 800,
                  color: "#374151",
                  marginBottom: 20,
                }}
              >
                검색 결과에서 다시 찾기
              </h3>
            </div>

            <div
              style={{
                background: "#fff",
                border: "1px solid #e5e7eb",
                borderRadius: 10,
                overflow: "hidden",
                margin: "0 0 10px",
              }}
            >
              {/* 검색어 */}
              <S.FilterRow>
                <div className="label">검색어</div>
                <div className="content" style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <input
                    type="text"
                    value={innerKeyword}
                    onChange={(e) => setInnerKeyword(e.target.value)}
                    placeholder="검색된 데이터 내 재 검색"
                    style={{
                      height: 36,
                      padding: "6px 10px",
                      border: "1px solid #e5e7eb",
                      borderRadius: 8,
                      width: 300,
                      maxWidth: "100%",
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => { resetFilters(); setCatShowCounts({}); }}
                    title="필터 초기화"
                    style={{
                      height: 36,
                      width: 36,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      border: "1px solid #e5e7eb",
                      borderRadius: "50%",
                      background: "#f9fafb",
                      color: "#374151",
                      cursor: "pointer",
                      fontSize: 18,
                      transition: "all 0.2s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "#e5e7eb";
                      e.currentTarget.style.color = "#111827";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "#f9fafb";
                      e.currentTarget.style.color = "#374151";
                    }}
                  >
                    <i className="fi fi-rs-refresh" />
                  </button>
                </div>
              </S.FilterRow>

              {/* 1차 카테고리 */}
              <S.FilterRow>
                <div className="label">카테고리</div>
                <div className="content">
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <button
                      type="button"
                      onClick={() => setSelectedTopCats([])}
                      style={{
                        padding: "6px 10px",
                        borderRadius: 999,
                        border: "1px solid #e5e7eb",
                        background: selectedTopCats.length === 0 ? "#111827" : "#fff",
                        color: selectedTopCats.length === 0 ? "#fff" : "#111827",
                        fontSize: 13,
                      }}
                    >
                      전체
                    </button>
                    {allTopCats.map((cat) => {
                      const active = selectedTopCats.includes(cat);
                      return (
                        <button
                          key={cat}
                          type="button"
                          onClick={() => {
                            toggleTopCat(cat);
                            setCatShowCounts((prev) => ({ ...prev, [cat]: prev[cat] || INITIAL_PAGE }));
                          }}
                          title={cat}
                          style={{
                            padding: "6px 10px",
                            borderRadius: 999,
                            border: "1px solid #e5e7eb",
                            background: active ? "#111827" : "#fff",
                            color: active ? "#fff" : "#111827",
                            fontSize: 13,
                          }}
                        >
                          {cat}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </S.FilterRow>

              {/* 2차 카테고리 */}
              <S.FilterRow>
                <div className="label">상세 카테고리</div>
                <div className="content">
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <button
                      type="button"
                      onClick={() => setSearchTags([])}
                      style={{
                        padding: "6px 10px",
                        borderRadius: 999,
                        border: "1px solid #e5e7eb",
                        background: searchTags.length === 0 ? "#111827" : "#fff",
                        color: searchTags.length === 0 ? "#fff" : "#111827",
                        fontSize: 13,
                      }}
                    >
                      전체
                    </button>
                    {allSecondTags.map((tag) => {
                      const active = searchTags.includes(tag);
                      return (
                        <button
                          key={tag}
                          type="button"
                          onClick={() => toggleTag(tag)}
                          title={tag}
                          style={{
                            padding: "6px 10px",
                            borderRadius: 999,
                            border: "1px solid #e5e7eb",
                            background: active ? "#111827" : "#fff",
                            color: active ? "#fff" : "#111827",
                            fontSize: 13,
                          }}
                        >
                          {tag}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </S.FilterRow>

              {/* 사이즈 + 오차 */}
              <S.FilterRow>
                <div className="label">사이즈</div>
                <div className="content" style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span>가로</span>
                    <input
                      type="text"
                      inputMode="numeric"
                      placeholder="mm"
                      value={garoInput}
                      onChange={(e) => setGaroInput(e.target.value)}
                      style={{ ...controlStyle, width: 90, maxWidth: "100%" }}
                    />
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span>세로</span>
                    <input
                      type="text"
                      inputMode="numeric"
                      placeholder="mm"
                      value={seroInput}
                      onChange={(e) => setSeroInput(e.target.value)}
                      style={{ ...controlStyle, width: 90, maxWidth: "100%" }}
                    />
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span>높이</span>
                    <input
                      type="text"
                      inputMode="numeric"
                      placeholder="mm"
                      value={highInput}
                      onChange={(e) => setHighInput(e.target.value)}
                      style={{ ...controlStyle, width: 90, maxWidth: "100%" }}
                    />
                  </div>

                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginLeft: 8 }}>
                    <select
                      id="sizeFilter"
                      name="sizeFilter"
                      value={sizeTol}
                      onChange={(e) => setSizeTol(e.target.value)}
                      style={{ ...controlStyle, minWidth: 90, maxWidth: "100%", background: "#fff" }}
                    >
                      <option value="0">± 0</option>
                      <option value="10">± 10</option>
                      <option value="20">± 20</option>
                      <option value="30">± 30</option>
                      <option value="40">± 40</option>
                      <option value="50">± 50</option>
                      <option value="60">± 60</option>
                    </select>
                  </div>
                </div>
              </S.FilterRow>
            </div>
          </>
        )}

        {/* 결과 상태바 (상품이 있을 때만 표시) */}
        {filteredTotal > 0 && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              margin: "6px 0 18px",
              whiteSpace: "nowrap",
              minWidth: 0,
            }}
          >
            <div style={{ color: "#6b7280", overflow: "hidden", textOverflow: "ellipsis" }}>
              총 <strong className="text-brand">{filteredTotal.toLocaleString()}</strong>개
            </div>
            <div style={{ flexShrink: 0 }}>
              <SortTabs />
            </div>
          </div>
        )}

        {/* 상품 & 커뮤니티 렌더 */}
        {renderFilteredGroups(
          filteredGroups,
          hasCommunity,
          searchList,
          cateViewStats,
          moreProducts,
          dummyList,
          catShowCounts,
          showMoreProducts,
          suggestions,
          handleSuggestClick,
          // ✅ 이동 함수 & router 전달
          moveToDetail,
          router
        )}
      </div>
    </S.Container>
  );
};

function renderFilteredGroups(
  filteredGroups,
  hasCommunity,
  searchList,
  cateViewStats,
  moreProducts,
  dummyList,
  catShowCounts,
  showMoreProducts,
  suggestions = [],
  onClickSuggest,
  moveToDetail,
  router
) {
  const total = Object.values(filteredGroups).reduce((s, a) => s + a.length, 0);

  // ✅ 부모가 moveToDetail 안 줄 때 fallback
  const goDetail = (id) => {
    if (typeof moveToDetail === "function") return moveToDetail(id);
    if (!router) return;
    if (id === undefined || id === null) return;
    router.push(`/esg-guide/post/${encodeURIComponent(id)}`);
  };

  return (
    <>
      {/* 상품 섹션 (카테고리별 더보기) */}
      {total === 0 ? (
        <S.ProductsGrid>
          <S.ProductsSignWrapper style={{ gridColumn: "1 / -1" }}>
            {Array.isArray(suggestions) && suggestions.length > 0 ? (
              <>
                <h4>찾으시는 상품이 없으신가요? 😢</h4>
                <p style={{ margin: "14px 0 28px", fontSize: 18, color: "#374151", textAlign: "center" }}>
                  우측 하단의 <strong style={{ color: "#10b981" }}>채널톡</strong>으로 문의 주시면<br />
                  친절히 도와드리거나 <strong style={{ color: "#10b981" }}>맞춤제작 서비스</strong>를 안내해드릴게요 💬
                </p>
                <div style={{ color: "#6b7280", marginBottom: 20, fontSize: 17, textAlign: "center" }}>
                  아래 추천 키워드 중 찾으시는 상품을 클릭해보세요
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
                    justifyContent: "center",
                    gap: 12,
                    margin: "0 auto 36px",
                    maxWidth: 820,
                    width: "100%",
                  }}
                >
                  {suggestions.map((kw, idx) => (
                    <button
                      key={`${kw}-${idx}`}
                      type="button"
                      onClick={() => onClickSuggest && onClickSuggest(kw)}
                      title={kw}
                      style={{
                        width: "100%",
                        minHeight: 56,
                        padding: "12px 16px",
                        borderRadius: 30,
                        border: "1px solid #e5e7eb",
                        background: "#fff",
                        cursor: "pointer",
                        fontSize: 15,
                        color: "#111827",
                        transition: "all 0.2s ease",
                        boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        textAlign: "center",
                        wordBreak: "break-word",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = "#f9fafb";
                        e.currentTarget.style.transform = "translateY(-2px)";
                        e.currentTarget.style.boxShadow = "0 3px 6px rgba(0,0,0,0.08)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = "#fff";
                        e.currentTarget.style.transform = "none";
                        e.currentTarget.style.boxShadow = "0 1px 2px rgba(0,0,0,0.04)";
                      }}
                    >
                      <span
                        style={{
                          display: "-webkit-box",
                          WebkitBoxOrient: "vertical",
                          WebkitLineClamp: 2,
                          overflow: "hidden",
                          lineHeight: 1.3,
                          maxHeight: "2.6em",
                        }}
                      >
                        {kw}
                      </span>
                    </button>
                  ))}
                </div>

                <a className="btn" href="/customizing/customizing-products/">
                  맞춤제작 바로가기
                </a>
              </>
            ) : (
              <>
                <h4>아쉽게도 조건에 맞는 상품을 찾지 못했어요 😥</h4>
                <p style={{ fontSize: 18, color: "#4b5563", lineHeight: 1.9, marginBottom: 36, textAlign: "center" }}>
                  그래도 걱정 마세요! 💬<br />
                  우측 하단의 <strong style={{ color: "#10b981" }}>채널톡</strong>으로 문의 주시면 담당자가 빠르게 도와드릴게요.<br />
                  또는 아래 <strong>맞춤제작 서비스</strong>를 이용하시면 원하시는 형태로 제작해드릴 수도 있어요 ✨
                </p>
                <a className="btn" href="/customizing/customizing-products/">
                  맞춤제작 바로가기
                </a>
              </>
            )}
          </S.ProductsSignWrapper>
        </S.ProductsGrid>
      ) : (
        Object.keys(filteredGroups).map((key, i) => {
          const list = filteredGroups[key] || [];
          const showCount = Math.min(catShowCounts?.[key] ?? INITIAL_PAGE, list.length);
          const canMore = showCount < list.length;

          return (
            <S.ListWraper key={i}>
              <S.CategoryTitle>
                {key}{" "}
                <span style={{ color: "#9CA3AF", fontWeight: 400 }}>
                  ({list.length.toLocaleString()}개)
                </span>
              </S.CategoryTitle>

              <S.ProductsGrid>
                {list.slice(0, showCount).map((item, idx) => (
                  <SingleProduct
                    key={`${key}-${idx}-${item?.pcode || item?.pname || idx}`}
                    product={item}
                    dummyList={dummyList}
                    listViewType={"grid"}
                  />
                ))}
              </S.ProductsGrid>

              {canMore && (
                <S.MoreBtn onClick={() => showMoreProducts(key)}>
                  {key} 더보기
                </S.MoreBtn>
              )}
            </S.ListWraper>
          );
        })
      )}

      {/* 커뮤니티 섹션 */}
      {hasCommunity && (() => {
        const all = searchList.community.data || [];
        const cStart = cateViewStats?.community?.start ?? 0;
        const cLast  = cateViewStats?.community?.last  ?? 8;

        const sliced = all.slice(cStart, cLast);
        const canMore = cLast < all.length;

        return (
          <S.ListWraper>
            <S.CategoryTitle>관련 게시물</S.CategoryTitle>
            <S.SearchedCommunity>
              {sliced.map((el, i) => (
                <S.CommunityBox
                  key={`${el.id || i}`}
                  onClick={() => goDetail(el.id)}             // ✅ 클릭 이동
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && goDetail(el.id)}
                >
                  {el.thumbnail && (
                    <div className="thumbnail">
                      <img src={el.thumbnail} alt="게시물 썸네일" />
                    </div>
                  )}
                  <div className="title">
                    <h5>{el.title}</h5>
                  </div>
                  <h6 className="sub_title">{el.sub_title}</h6>
                </S.CommunityBox>
              ))}
            </S.SearchedCommunity>

            {canMore && (
              <S.MoreBtn onClick={() => moreProducts && moreProducts("community")}>
                검색된 게시물 더보기
              </S.MoreBtn>
            )}
          </S.ListWraper>
        );
      })()}
    </>
  );
}

export default SearchUI;